/**
 * ChatWindow - Chat-Fenster für AI Chatbot
 * Wird pro Seite individuell kontextualisiert
 * Minimierbar/Maximierbar mit Animationen
 */

import React, { useState, useEffect, useRef } from 'react';
import { ChatService } from '../../services/ChatService.js';
import DeleteConfirmModal from '../ui/DeleteConfirmModal.jsx';
import { debugLog } from '../../utils/debugLog.js';

const ChatWindow = ({ 
    supabase, 
    authManager, 
    organizationService, 
    placement = 'fixed',
    pageKey = 'models',
    contextProvider = null,
    enableModelContext
}) => {
    const [viewMode, setViewMode] = useState('minimized'); // 'minimized' | 'maximized' | 'fullscreen'
    const [isAnimating, setIsAnimating] = useState(false);
    const [userName, setUserName] = useState('');
    const [isLoadingName, setIsLoadingName] = useState(true);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [chatService, setChatService] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [activeSessionId, setActiveSessionId] = useState(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [traceModalMessageIndex, setTraceModalMessageIndex] = useState(null);
    const [showIntroBubble, setShowIntroBubble] = useState(true);
    const [, setLanguageTick] = useState(0);
    const [clearChatConfirmOpen, setClearChatConfirmOpen] = useState(false);
    const messagesEndRef = useRef(null);
    const abortControllerRef = useRef(null);
    const traceModalRef = useRef(null);
    const traceModalCloseRef = useRef(null);
    const inputRef = useRef(null);
    const historyPanelRef = useRef(null);
    const historyButtonRef = useRef(null);
    const sendingRef = useRef(false);

    const resolvedPageKey = pageKey || 'global';
    const useModelContext = (enableModelContext !== undefined) ? enableModelContext : !contextProvider;
    const t = (key, fallback, params = {}) => {
        if (window.i18n && typeof window.i18n.t === 'function') {
            return window.i18n.t(key, params);
        }
        return fallback;
    };

    // Initialisiere ChatService und lade Daten beim Mount
    useEffect(() => {
        loadUserName();
        initializeChatService();
        debugLog('✅ ChatWindow-Komponente wurde gemountet, placement:', placement);

        // Intro-Sprechblase nach kurzer Zeit automatisch ausblenden
        const t = setTimeout(() => setShowIntroBubble(false), 5500);
        const onLanguageChanged = () => setLanguageTick(prev => prev + 1);
        window.addEventListener('languageChanged', onLanguageChanged);
        return () => {
            clearTimeout(t);
            window.removeEventListener('languageChanged', onLanguageChanged);
        };
    }, []);

    // Lade Chat-Verlauf wenn Service initialisiert ist
    useEffect(() => {
        if (chatService && viewMode !== 'minimized') {
            loadChatHistory();
            loadSessions();
        }
    }, [chatService, viewMode]);

    // Scroll zu letzter Nachricht
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Trace-Modal: Fokus beim Öffnen, Escape schließt, Focus Trap
    useEffect(() => {
        if (traceModalMessageIndex == null) return;
        const closeModal = () => setTraceModalMessageIndex(null);
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                closeModal();
                return;
            }
            if (e.key !== 'Tab' || !traceModalRef.current) return;
            const modal = traceModalRef.current;
            const focusable = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last && last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first && first.focus();
                }
            }
        };
        const t = setTimeout(() => {
            if (traceModalCloseRef.current) traceModalCloseRef.current.focus();
        }, 50);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            clearTimeout(t);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [traceModalMessageIndex]);

    // History Panel: Outside Click + Escape
    useEffect(() => {
        if (!isHistoryOpen) return;
        const handleOutsideClick = (e) => {
            if (historyPanelRef.current && historyPanelRef.current.contains(e.target)) return;
            if (historyButtonRef.current && historyButtonRef.current.contains(e.target)) return;
            setIsHistoryOpen(false);
        };
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setIsHistoryOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isHistoryOpen]);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const initializeChatService = async () => {
        try {
            if (!supabase || !organizationService) {
                console.warn('ChatService: supabase oder organizationService fehlt');
                return;
            }

            const orgId = organizationService.getCurrentOrganizationId();
            if (!orgId) {
                console.warn('ChatService: Keine Organisation ausgewählt');
                return;
            }

            debugLog('ChatService: Initialisiere mit orgId:', orgId);

            const service = new ChatService();
            await service.init(supabase, orgId, { pageKey: resolvedPageKey });
            setChatService(service);
            if (typeof window !== 'undefined') window.ChatService = ChatService;
            updateChatContext(service);
            debugLog('ChatService: Erfolgreich initialisiert');
        } catch (error) {
            console.error('Fehler beim Initialisieren des ChatService:', error);
        }
    };

    const updateChatContext = (service) => {
        if (!service) return;
        
        try {
            const org = organizationService ? organizationService.getCurrentOrganization() : null;
            const orgName = org ? org.name : '';
            
            let context = {
                organizationName: orgName,
                organizationId: organizationService ? organizationService.getCurrentOrganizationId() : null,
                pageKey: resolvedPageKey
            };
            
            // Model-Kontext (optional)
            if (useModelContext && window.modelService) {
                const modelId = window.modelService.getModelId();
                // Hole Model-Name aus modelsPage oder lasse es leer
                let modelName = '';
                if (window.modelsPage && window.modelsPage.model) {
                    modelName = window.modelsPage.model.name || '';
                }
                
                context.modelId = modelId;
                context.modelName = modelName;
                
                // Sections und Variablen
                if (window.modelsPage && window.modelsPage.sections) {
                    const sections = window.modelsPage.sections;
                    const variables = [];
                    
                    sections.forEach(section => {
                        if (section.variables && Array.isArray(section.variables)) {
                            section.variables.forEach(variable => {
                                variables.push({
                                    sectionName: section.name,
                                    variableName: variable.variableName || variable.name
                                });
                            });
                        }
                    });
                    
                    context.variables = variables;
                    context.sections = sections.map(s => s.name);
                }
                
                // Aktuelles Jahr
                if (window.modelsPage && window.modelsPage.currentYear) {
                    context.year = window.modelsPage.currentYear;
                } else {
                    context.year = new Date().getFullYear();
                }
            }

            // Optionaler Context Provider (überschreibt / erweitert)
            if (typeof contextProvider === 'function') {
                const provided = contextProvider({
                    supabase,
                    authManager,
                    organizationService
                });
                if (provided && typeof provided === 'object') {
                    context = {
                        ...context,
                        ...provided
                    };
                }
                if (!context.pageKey) {
                    context.pageKey = resolvedPageKey;
                }
            }
            
            service.updateContext(context);
        } catch (error) {
            console.error('Fehler beim Aktualisieren des Chat-Kontexts:', error);
        }
    };

    // Aktualisiere Kontext wenn sich Model oder Jahr ändert
    useEffect(() => {
        if (!chatService) return;

        // Initiale Kontext-Aktualisierung
        updateChatContext(chatService);

        if (!useModelContext) return;
        
        // Event-Listener für Model-Änderungen (falls verfügbar)
        const handleModelChange = () => {
            updateChatContext(chatService);
        };
        
        // Höre auf Custom Events für Model-Änderungen
        window.addEventListener('modelChanged', handleModelChange);
        window.addEventListener('yearChanged', handleModelChange);
        
        return () => {
            window.removeEventListener('modelChanged', handleModelChange);
            window.removeEventListener('yearChanged', handleModelChange);
        };
    }, [chatService, useModelContext]);

    // Rechtsklick "Drill down by Siqment Engine": Chat öffnen mit Zell-Kontext, LLM schlägt Spalten vor
    const handleSegmentEngineDrilldownOpenRef = useRef(null);
    handleSegmentEngineDrilldownOpenRef.current = async (detail) => {
        if (!useModelContext) return;
        if (!chatService || !detail || !detail.variableName) return;
        updateChatContext(chatService);
        chatService.updateContext({
            lastUsedVariable: detail.variableName,
            lastUsedFilters: { year: detail.year, month: detail.month },
            segmentEngineDrilldownRequest: true
        });
        setViewMode('maximized');
        const initialMessage = t('chat.initialDrilldownQuestion', 'Welche Möglichkeiten gibt es, diese Zelle nach Spalten aufzuschlüsseln oder zu analysieren?');
        setTimeout(() => {
            handleSuggestionClick(initialMessage);
        }, 400);
    };
    useEffect(() => {
        if (!useModelContext) return;
        const handler = (e) => {
            const detail = e.detail;
            if (detail && handleSegmentEngineDrilldownOpenRef.current) {
                handleSegmentEngineDrilldownOpenRef.current(detail);
            }
        };
        window.addEventListener('segmentEngineDrilldownOpen', handler);
        return () => window.removeEventListener('segmentEngineDrilldownOpen', handler);
    }, [chatService, useModelContext]);

    const loadChatHistory = async () => {
        if (!chatService) return;
        if (!chatService.currentSessionId) {
            setMessages([]);
            return;
        }
        
        try {
            const history = await chatService.loadHistory(50);
            setMessages(history || []);
        } catch (error) {
            console.error('Fehler beim Laden des Chat-Verlaufs:', error);
        }
    };

    const loadSessions = async () => {
        if (!chatService) return;
        setIsLoadingSessions(true);
        try {
            const list = await chatService.listSessions(50);
            setSessions(list || []);
            setActiveSessionId(chatService.currentSessionId || null);
        } catch (error) {
            console.error('Fehler beim Laden der Sessions:', error);
        } finally {
            setIsLoadingSessions(false);
        }
    };

    const handleNewChat = async () => {
        if (!chatService) return;
        try {
            const newSession = await chatService.createNewSession(null);
            setActiveSessionId(newSession.id);
            setMessages([]);
            setIsHistoryOpen(false);
            await loadSessions();
        } catch (error) {
            console.error('Fehler beim Erstellen der Session:', error);
        }
    };

    const handleSessionChange = async (sessionId) => {
        if (!chatService || !sessionId) return;
        const session = sessions.find(s => s.id === sessionId) || sessionId;
        await chatService.switchSession(session);
        setActiveSessionId(sessionId);
        setIsHistoryOpen(false);
        await loadChatHistory();
    };

    const formatSessionTitle = (session) => {
        if (!session) return t('chat.sessionLabel', 'Chat');
        if (session.title && String(session.title).trim()) return session.title;
        const locale = (window.i18n && window.i18n.currentLanguage === 'en') ? 'en-US' : 'de-DE';
        const createdAt = session.created_at ? new Date(session.created_at) : null;
        return createdAt ? `${t('chat.sessionLabel', 'Chat')} ${createdAt.toLocaleDateString(locale)}` : t('chat.sessionLabel', 'Chat');
    };

    const formatSessionMeta = (session) => {
        if (!session) return '';
        const locale = (window.i18n && window.i18n.currentLanguage === 'en') ? 'en-US' : 'de-DE';
        const dateValue = session.updated_at || session.created_at;
        if (!dateValue) return '';
        const date = new Date(dateValue);
        return date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim() || isSending || !chatService) {
            return;
        }
        if (sendingRef.current) return;
        sendingRef.current = true;

        const userMessage = inputValue.trim();
        try {
            if (!chatService.currentSessionId) {
                const newSession = await chatService.createNewSession(null);
                setActiveSessionId(newSession.id);
                await loadSessions();
            }
        } catch (error) {
            console.error('Fehler beim Erstellen der Session:', error);
            sendingRef.current = false;
            window.alert(t('chat.startError', 'Chat konnte nicht gestartet werden: {error}', { error: error.message || t('app.unknownError', 'Unbekannter Fehler') }));
            return;
        }
        setInputValue('');
        setIsSending(true);

        // Erstelle neuen AbortController für diese Anfrage
        abortControllerRef.current = new AbortController();

        // Füge User-Nachricht sofort zum UI hinzu
        const newUserMessage = {
            role: 'user',
            content: userMessage,
            created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, newUserMessage]);

        // Lade-Anzeige: nur Punkte-Animation (kein Text)
        const processingMessage = {
            role: 'assistant',
            content: '',
            created_at: new Date().toISOString(),
            isProcessing: true
        };
        setMessages(prev => [...prev, processingMessage]);

        try {
            // Sende Nachricht an Backend mit AbortSignal
            const assistantMessage = await chatService.sendMessage(userMessage, abortControllerRef.current.signal);
            
            // Entferne "Wird verarbeitet..." Nachricht und füge echte Antwort hinzu
            setMessages(prev => {
                const filtered = prev.filter(msg => !msg.isProcessing);
                return [...filtered, assistantMessage];
            });
        } catch (error) {
            console.error('Fehler beim Senden der Nachricht:', error);
            
            // Entferne "Wird verarbeitet..." Nachricht
            setMessages(prev => prev.filter(msg => !msg.isProcessing));
            
            // Zeige Fehler-Nachricht
            const errorMessage = {
                role: 'assistant',
                content: error.message && error.message.includes('abgebrochen') 
                    ? 'Anfrage wurde abgebrochen.'
                    : t('chat.sendError', 'Entschuldigung, es gab einen Fehler beim Senden Ihrer Nachricht. Bitte versuchen Sie es erneut.'),
                created_at: new Date().toISOString()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsSending(false);
            abortControllerRef.current = null;
            sendingRef.current = false;
            if (chatService) {
                loadSessions();
            }
        }
    };

    const handleSuggestionClick = async (payload) => {
        if (!payload || isSending || !chatService) return;
        if (sendingRef.current) return;
        sendingRef.current = true;
        try {
            if (!chatService.currentSessionId) {
                const newSession = await chatService.createNewSession(null);
                setActiveSessionId(newSession.id);
                await loadSessions();
            }
        } catch (error) {
            console.error('Fehler beim Erstellen der Session:', error);
            sendingRef.current = false;
            window.alert(t('chat.startError', 'Chat konnte nicht gestartet werden: {error}', { error: error.message || t('app.unknownError', 'Unbekannter Fehler') }));
            return;
        }
        const displayContent = typeof payload === 'object'
            ? (payload.label || payload.text || '[Auswahl]')
            : payload;
        const userMessage = {
            role: 'user',
            content: displayContent,
            created_at: new Date().toISOString(),
            ...(typeof payload === 'object' ? { metadata: { payload } } : {})
        };
        setMessages(prev => [...prev, userMessage]);
        setIsSending(true);
        abortControllerRef.current = new AbortController();
        const processingMessage = {
            role: 'assistant',
            content: '',
            created_at: new Date().toISOString(),
            isProcessing: true
        };
        setMessages(prev => [...prev, processingMessage]);
        try {
            const assistantMessage = await chatService.sendMessage(payload, abortControllerRef.current.signal);
            setMessages(prev => {
                const filtered = prev.filter(msg => !msg.isProcessing);
                return [...filtered, assistantMessage];
            });
        } catch (error) {
            console.error('Fehler beim Senden der Suggestion:', error);
            setMessages(prev => prev.filter(msg => !msg.isProcessing));
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: error.message && error.message.includes('abgebrochen')
                    ? t('chat.requestAborted', 'Anfrage wurde abgebrochen.')
                    : t('chat.genericError', 'Entschuldigung, es gab einen Fehler. Bitte versuchen Sie es erneut.'),
                created_at: new Date().toISOString()
            }]);
        } finally {
            setIsSending(false);
            abortControllerRef.current = null;
            sendingRef.current = false;
            if (chatService) {
                loadSessions();
            }
        }
    };

    const handleClearChat = () => {
        if (!chatService) return;
        setClearChatConfirmOpen(true);
    };

    const handleConfirmClearChat = async () => {
        setClearChatConfirmOpen(false);
        if (!chatService) return;
        const sessionId = chatService.currentSessionId;
        if (!sessionId) return;
        try {
            await chatService.deleteSession(sessionId);
            setActiveSessionId(null);
            setMessages([]);
            setIsHistoryOpen(false);
            await loadSessions();
        } catch (error) {
            console.error('Fehler beim Löschen des Chats:', error);
            window.alert(t('chat.deleteError', 'Chat konnte nicht gelöscht werden: {error}', { error: error.message || t('app.unknownError', 'Unbekannter Fehler') }));
        }
    };

    const handleCancelMessage = () => {
        if (abortControllerRef.current && isSending) {
            abortControllerRef.current.abort();
            setIsSending(false);
            
            // Entferne "Wird verarbeitet..." Nachricht
            setMessages(prev => {
                const filtered = prev.filter(msg => !msg.isProcessing);
                // Füge Abbruch-Nachricht hinzu
                return [...filtered, {
                    role: 'assistant',
                    content: 'Anfrage wurde abgebrochen.',
                    created_at: new Date().toISOString()
                }];
            });
            
            abortControllerRef.current = null;
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Textarea-Höhe an Inhalt anpassen (vertikal wachsend bis max)
    const adjustInputHeight = () => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        const max = 160; // max Höhe in px (~6 Zeilen)
        const h = Math.min(max, Math.max(44, el.scrollHeight));
        el.style.height = h + 'px';
    };

    useEffect(() => {
        adjustInputHeight();
    }, [inputValue]);

    // Erkennt Markdown-Tabellenzeilen: | a | b | c |
    const isTableRow = (line) => {
        if (!line || typeof line !== 'string') return false;
        const t = line.trim();
        return t.startsWith('|') && t.endsWith('|') && t.length > 2;
    };

    // Prüft ob Zeile ein Trennstrich-Separator ist (|---|---|)
    const isTableSeparator = (line) => {
        if (!line || typeof line !== 'string') return false;
        const t = line.trim();
        if (!t.startsWith('|') || !t.endsWith('|')) return false;
        const cells = t.slice(1, -1).split('|').map((c) => c.trim());
        return cells.length >= 1 && cells.every((c) => /^-+$/.test(c));
    };

    // Parst Content in Blöcke: Text oder Tabelle
    const parseContentBlocks = (content) => {
        if (!content || typeof content !== 'string') return [];
        const lines = content.split('\n');
        const blocks = [];
        let i = 0;
        while (i < lines.length) {
            if (isTableRow(lines[i])) {
                const tableLines = [];
                while (i < lines.length && (isTableRow(lines[i]) || isTableSeparator(lines[i]))) {
                    tableLines.push(lines[i]);
                    i++;
                }
                blocks.push({ type: 'table', lines: tableLines });
            } else {
                const textLines = [];
                while (i < lines.length && !isTableRow(lines[i])) {
                    textLines.push(lines[i]);
                    i++;
                }
                if (textLines.length > 0) {
                    blocks.push({ type: 'text', lines: textLines });
                }
            }
        }
        return blocks;
    };

    // Rendert eine Markdown-Tabelle als <table>
    const renderTable = (tableLines, options) => {
        if (!tableLines || tableLines.length === 0) return null;
        const rows = tableLines
            .filter((line) => isTableRow(line) && !isTableSeparator(line))
            .map((line) => {
                const cells = line
                    .trim()
                    .slice(1, -1)
                    .split('|')
                    .map((c) => c.trim());
                return cells;
            });
        if (rows.length === 0) return null;
        const headerRow = rows[0];
        const bodyRows = rows.slice(1);
        return (
            <div className="chat-message-table-wrap">
                <table className="chat-message-table">
                    <thead>
                        <tr>
                            {headerRow.map((cell, j) => (
                                <th key={j}>{cell}</th>
                            ))}
                        </tr>
                    </thead>
                    {bodyRows.length > 0 && (
                        <tbody>
                            {bodyRows.map((row, ri) => (
                                <tr key={ri}>
                                    {row.map((cell, j) => (
                                        <td key={j}>{cell}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    )}
                </table>
            </div>
        );
    };

    // Minimal markdown: **bold** -> <strong>; bei Assistant zusätzlich Variable/Konto hervorheben
    const renderMessageLine = (line, options) => {
        if (!line || typeof line !== 'string') return line;
        const usedVariable = (options && options.usedVariable) || null;
        const parts = line.split(/\*\*(.+?)\*\*/g);
        const result = [];
        for (let i = 0; i < parts.length; i++) {
            if (i % 2 === 1) {
                result.push(<strong key={i}>{parts[i]}</strong>);
            } else {
                const segment = parts[i];
                if (!segment) continue;
                const hasKonto = /Konto\s+\d+/i.test(segment);
                if (!usedVariable && !hasKonto) {
                    result.push(segment);
                    continue;
                }
                const escapedVar = usedVariable ? usedVariable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : '';
                const entityRegex = new RegExp('(Konto\\s+\\d+' + (escapedVar ? '|' + escapedVar + '\\b' : '') + ')', 'gi');
                const segParts = segment.split(entityRegex);
                segParts.forEach((sub, j) => {
                    const key = 's' + i + '-' + j;
                    if (!sub) return;
                    if (j % 2 === 1) {
                        result.push(<span key={key} className="chat-highlight-entity">{sub}</span>);
                    } else {
                        result.push(sub);
                    }
                });
            }
        }
        return result.length === 1 ? result[0] : result;
    };

    // Kurzfassung für Trace-Anzeige (kein vollständiges result rendern)
    const formatTraceResultSummary = (result) => {
        if (result == null) return '–';
        if (Array.isArray(result)) return `Ergebnis: ${result.length} Zeilen`;
        if (Array.isArray(result.data)) return `Ergebnis: ${result.data.length} Zeilen`;
        if (typeof result === 'object') {
            const keys = Object.keys(result);
            if (keys.length === 0) return 'Ergebnis: leeres Objekt';
            if (keys.length <= 3) return `Ergebnis: ${keys.join(', ')}`;
            return `Ergebnis: Objekt mit ${keys.length} Feldern (${keys.slice(0, 2).join(', ')}, …)`;
        }
        if (typeof result === 'string' && result.length > 80) return `Ergebnis: ${result.slice(0, 80)}…`;
        return String(result);
    };

    const renderMessage = (message, index) => {
        // Verarbeitungs-Platzhalter: nur Punkte-Animation, kein Text
        if (message.isProcessing) {
            return (
                <div key={index} className="chat-message chat-message-assistant">
                    <div className="chat-message-content">
                        <div className="chat-message-typing">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                </div>
            );
        }
        const isUser = message.role === 'user';
        const hasChart = message.metadata && message.metadata.chart;
        const usedVariable = message.metadata && message.metadata.usedVariable;
        const usedFilters = message.metadata && message.metadata.usedFilters;
        const usedColumn = message.metadata && message.metadata.usedColumn;
        const usedGroupBy = message.metadata && message.metadata.usedGroupBy;
        const hasSource = usedVariable || usedColumn || usedGroupBy || (usedFilters && (usedFilters.year || usedFilters.month));
        // User-Nachricht: Wenn vorherige Nachricht Suggestion-Buttons hatte und Inhalt ein Payload ist, Label anzeigen (z. B. "Ja, Daten laden" statt "confirm_variable")
        let displayContent = message.content;
        if (isUser && message.metadata && message.metadata.payload) {
            const payload = message.metadata.payload;
            if (payload && typeof payload === 'object') {
                displayContent = payload.label || payload.text || displayContent;
            }
        }
        if (isUser && index > 0 && message.content) {
            const prev = messages[index - 1];
            if (prev && prev.role === 'assistant' && prev.metadata && prev.metadata.suggestions && prev.metadata.suggestions.length) {
                const suggestion = prev.metadata.suggestions.find(s => (s.payload != null ? s.payload : s.label) === message.content);
                if (suggestion) displayContent = suggestion.label;
            }
        }
        const lineOptions = isUser ? null : { usedVariable: message.metadata && message.metadata.usedVariable };
        const blocks = displayContent ? parseContentBlocks(displayContent) : [];

        return (
            <div key={index} className={`chat-message ${isUser ? 'chat-message-user' : 'chat-message-assistant'}`}>
                <div className="chat-message-content">
                    <div className="chat-message-text">
                        {blocks.map((block, bi) => {
                            if (block.type === 'table') {
                                return <React.Fragment key={bi}>{renderTable(block.lines, lineOptions)}</React.Fragment>;
                            }
                            return (
                                <React.Fragment key={bi}>
                                    {block.lines.map((line, li) => (
                                        <React.Fragment key={li}>
                                            {renderMessageLine(line, lineOptions)}
                                            {li < block.lines.length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </div>
                    {hasSource && (
                        <div className="chat-message-source">
                            {t('chat.source', 'Quelle')}: {usedVariable && <strong>{usedVariable}</strong>}
                            {usedVariable && (usedFilters || usedColumn || usedGroupBy) && ' · '}
                            {usedFilters && usedFilters.year && `${t('chat.year', 'Jahr')} ${usedFilters.year}`}
                            {usedFilters && usedFilters.month && `, ${t('chat.month', 'Monat')} ${usedFilters.month}`}
                            {usedFilters && usedFilters.months && usedFilters.months.length > 0 && `, ${t('chat.months', 'Monate')} ${usedFilters.months.join(', ')}`}
                            {usedColumn && ` · ${t('chat.column', 'Spalte')} ${usedColumn}`}
                            {usedGroupBy && ` · ${t('chat.groupBy', 'Aufschlüsselung nach')} ${usedGroupBy}`}
                        </div>
                    )}
                    {!isUser && message.metadata && message.metadata.function_calls && message.metadata.function_calls.length > 0 && (
                        <div className="chat-message-trace-inline">
                            <details>
                                <summary>{t('chat.showTrace', 'Trace anzeigen')}</summary>
                                <div className="chat-trace-inline-list">
                                    {message.metadata.function_calls.map((fc, idx) => (
                                        <div key={idx} className="chat-trace-inline-item">
                                            <div className="chat-trace-inline-name">{fc.name}</div>
                                            <div className="chat-trace-inline-args">
                                                <pre>{JSON.stringify(fc.arguments || {}, null, 2)}</pre>
                                            </div>
                                            <div className="chat-trace-inline-result">{fc.result}</div>
                                        </div>
                                    ))}
                                </div>
                            </details>
                        </div>
                    )}
                    {message.metadata && message.metadata.suggestions && message.metadata.suggestions.length > 0 && (!messages[index + 1] || messages[index + 1].role !== 'user') && (
                        <div className="chat-message-suggestions">
                            <span className="chat-message-suggestions-label">{t('chat.followUpQuestions', 'Folgefragen:')}</span>
                            {message.metadata.suggestions.map((s, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    className="chat-suggestion-button"
                                    disabled={isSending}
                                    onClick={() => handleSuggestionClick(s.payload != null ? s.payload : s.label)}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    )}
                    {hasChart && renderChart(message.metadata.chart)}
                </div>
                <div className="chat-message-footer">
                    <div className="chat-message-time">
                        {new Date(message.created_at).toLocaleTimeString(
                            (window.i18n && typeof window.i18n.getCurrentLanguage === 'function' && window.i18n.getCurrentLanguage() === 'en') ? 'en-US' : 'de-DE',
                            { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                        })}
                    </div>
                    {!isUser && (
                        <button
                            type="button"
                            className="chat-message-trace-btn"
                            onClick={() => setTraceModalMessageIndex(index)}
                            aria-label={t('chat.traceTitle', 'Was hat die KI gemacht?')}
                            title={t('chat.traceTitle', 'Was hat die KI gemacht?')}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderChart = (chartConfig) => {
        if (!chartConfig || !chartConfig.data || chartConfig.data.length === 0) {
            return null;
        }

        const chartType = chartConfig.type || 'area';
        const chartData = chartConfig.data || [];
        const config = chartConfig.config || {};

        // Rendere entsprechenden Chart-Typ
        if (chartType === 'area' && window.NeonAreaChart) {
            return (
                <div className="chat-message-chart">
                    <window.NeonAreaChart data={chartData} config={config} />
                </div>
            );
        }
        
        if (chartType === 'composed' && window.ComposedChart) {
            return (
                <div className="chat-message-chart">
                    <window.ComposedChart data={chartData} config={config} />
                </div>
            );
        }
        
        if (chartType === 'stacked' && window.StackedBarChart) {
            return (
                <div className="chat-message-chart">
                    <window.StackedBarChart data={chartData} config={config} />
                </div>
            );
        }
        
        if ((chartType === 'pie' || chartType === 'donut') && window.DonutChart) {
            return (
                <div className="chat-message-chart">
                    <window.DonutChart 
                        data={chartData} 
                        config={{
                            ...config,
                            innerRadius: chartType === 'donut' ? 60 : 0
                        }} 
                    />
                </div>
            );
        }
        
        return (
            <div className="chat-message-chart-error">
                {t('chat.chartTypeUnsupported', 'Chart-Typ "{type}" wird nicht unterstützt', { type: chartType })}
            </div>
        );
    };

    const loadUserName = async () => {
        try {
            setIsLoadingName(true);
            
            if (!supabase || !authManager || !authManager.currentUser) {
                // Fallback: Verwende E-Mail oder "Benutzer"
                let fallbackName = t('chat.userFallback', 'Benutzer');
                if (authManager && authManager.currentUser && authManager.currentUser.email) {
                    fallbackName = authManager.currentUser.email.split('@')[0];
                }
                setUserName(fallbackName);
                setIsLoadingName(false);
                return;
            }

            // Versuche Vor- und Nachname aus new_user_organizations zu laden
            if (organizationService) {
                const orgId = organizationService.getCurrentOrganizationId();
                if (orgId) {
                    const { data, error } = await supabase
                        .from('new_user_organizations')
                        .select('first_name, last_name')
                        .eq('user_id', authManager.currentUser.id)
                        .eq('organization_id', orgId)
                        .single();

                    if (!error && data) {
                        const firstName = data.first_name || '';
                        const lastName = data.last_name || '';
                        const fullName = `${firstName} ${lastName}`.trim();
                        
                        if (fullName) {
                            setUserName(fullName);
                            setIsLoadingName(false);
                            return;
                        }
                    }
                }
            }

            // Fallback: Lade aus user_metadata
            const metadata = authManager.currentUser.user_metadata || {};
            const firstName = metadata.first_name || '';
            const lastName = metadata.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim();

            if (fullName) {
                setUserName(fullName);
            } else {
                // Letzter Fallback: E-Mail oder "Benutzer"
                let emailName = t('chat.userFallback', 'Benutzer');
                if (authManager.currentUser && authManager.currentUser.email) {
                    emailName = authManager.currentUser.email.split('@')[0];
                }
                setUserName(emailName);
            }
        } catch (error) {
            console.error('Fehler beim Laden des Benutzernamens:', error);
            // Fallback bei Fehler
            let fallbackName = t('chat.userFallback', 'Benutzer');
            if (authManager && authManager.currentUser && authManager.currentUser.email) {
                fallbackName = authManager.currentUser.email.split('@')[0];
            }
            setUserName(fallbackName);
        } finally {
            setIsLoadingName(false);
        }
    };

    const isMinimized = viewMode === 'minimized';

    const handleToggle = () => {
        if (isAnimating) return; // Verhindere mehrfaches Klicken während Animation
        
        if (isMinimized) {
            // Beim Öffnen: erst maximiert
            setViewMode('maximized');
            setShowIntroBubble(false);
        } else {
            // Beim Minimieren: Setze animating Flag und dann State
            setIsAnimating(true);
            setTimeout(() => {
                setViewMode('minimized');
                setTimeout(() => {
                    setIsAnimating(false);
                }, 250);
            }, 10);
        }
    };

    const handleClose = () => {
        if (isAnimating) return;
        
        setIsAnimating(true);
        setTimeout(() => {
            setViewMode('minimized');
            setTimeout(() => {
                setIsAnimating(false);
            }, 250);
        }, 10);
    };

    const handleFullscreenToggle = () => {
        if (viewMode === 'fullscreen') {
            setViewMode('maximized');
        } else {
            setViewMode('fullscreen');
        }
    };

    // Wenn minimiert: Zeige nur Icon-Button
    if (viewMode === 'minimized') {
        // Immer fixed position, damit Button immer sichtbar bleibt
        const containerStyle = {
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 99999
        };
        
        return (
            <div style={containerStyle} className={`chat-window-container chat-window-minimized ${isAnimating ? 'chat-window-animating' : ''}`}>
                {showIntroBubble && (
                    <div className="chat-intro-bubble-wrapper">
                        <button
                            type="button"
                            className="chat-intro-bubble-close"
                            onClick={() => setShowIntroBubble(false)}
                            aria-label={t('chat.closeHint', 'Hinweis schließen')}
                        >
                            ×
                        </button>
                        <div className="chat-intro-bubble">
                            <div className="chat-intro-bubble-text">
                                {t('chat.introMessage', '👋 Ich bin da, um dir zu helfen. Stell mir einfach deine Fragen.')}
                            </div>
                            <div className="chat-intro-bubble-arrow" />
                        </div>
                    </div>
                )}
                <button
                    className="chat-window-icon-button chat-window-siqment-engine-button"
                    onClick={handleToggle}
                    aria-label={t('chat.openEngine', 'Siqment Engine öffnen')}
                    title={t('chat.openEngine', 'Siqment Engine öffnen')}
                >
                    <span className="chat-window-siqment-engine-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="chat-engine-icon">
                            <path d="M20 5c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h2v3l3.5-3H18c1.1 0 2-.9 2-2V5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="currentColor" fillOpacity="0.08"/>
                            <path d="M15.5 8.5l1.5 1.5-1.5 1.5M10 10h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="8" cy="10" r="0.9" fill="currentColor"/>
                        </svg>
                    </span>
                    <span className="chat-window-siqment-engine-label">Siqment Engine</span>
                </button>
            </div>
        );
    }

    // Wenn maximiert oder Vollbild: Zeige vollständiges Chat-Fenster
    const isFullscreen = viewMode === 'fullscreen';
    const containerClass = `chat-window-container chat-window-maximized ${isFullscreen ? 'chat-window-fullscreen' : ''} ${isAnimating ? 'chat-window-animating' : ''}`;
    const containerStyle = {
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 99999
    };
        
    return (
        <div className={containerClass} style={containerStyle}>
            <div className="chat-window">
                {/* Header */}
                <div className="chat-window-header">
                    <div className="chat-window-header-title">
                        <span className="chat-window-header-icon" aria-hidden>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="chat-engine-icon">
                                <path d="M20 5c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h2v3l3.5-3H18c1.1 0 2-.9 2-2V5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="currentColor" fillOpacity="0.1"/>
                                <path d="M15.5 8.5l1.5 1.5-1.5 1.5M10 10h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <circle cx="8" cy="10" r="0.9" fill="currentColor"/>
                            </svg>
                        </span>
                        <div className="chat-window-header-text">
                            <div className="chat-window-header-title-row">
                                <h3 className="chat-window-header-name">Siqment Engine</h3>
                                <span className="chat-window-beta">BETA</span>
                            </div>
                        </div>
                    </div>
                    <div className="chat-window-header-actions">
                        <button
                            type="button"
                            className="chat-window-button-newchat"
                            onClick={handleNewChat}
                            aria-label={t('chat.newChat', 'Neuen Chat starten')}
                            title={t('chat.newChat', 'Neuen Chat starten')}
                        >
                            +
                        </button>
                        <button
                            className="chat-window-button-history"
                            onClick={() => setIsHistoryOpen(prev => !prev)}
                            aria-label={t('chat.openHistory', 'Chat-Verlauf öffnen')}
                            title={t('chat.history', 'Chat-Verlauf')}
                            ref={historyButtonRef}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 12a9 9 0 1 0 3-6.7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M3 4v5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        <button
                            className="chat-window-button-clear"
                            onClick={handleClearChat}
                            aria-label={t('chat.clear', 'Chat löschen')}
                            title={t('chat.clearDescription', 'Chat löschen – Chat entfernen')}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span className="chat-window-button-clear-label">{t('chat.clear', 'Chat löschen')}</span>
                        </button>
                        <button
                            className="chat-window-button-fullscreen"
                            onClick={handleFullscreenToggle}
                            aria-label={isFullscreen ? t('chat.exitFullscreen', 'Fenster verkleinern') : t('chat.fullscreen', 'Vollbild')}
                            title={isFullscreen ? t('chat.exitFullscreen', 'Fenster verkleinern') : t('chat.fullscreen', 'Vollbild')}
                        >
                            {isFullscreen ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 3V5H5V8H3V5C3 3.89543 3.89543 3 5 3H8ZM16 3H19C20.1046 3 21 3.89543 21 5V8H19V5H16V3ZM21 16V19C21 20.1046 20.1046 21 19 21H16V19H19V16H21ZM5 16V19H8V21H5C3.89543 21 3 20.1046 3 19V16H5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 3H5C3.89543 3 3 3.89543 3 5V8M21 8V5C21 3.89543 20.1046 3 19 3H16M16 21H19C20.1046 21 21 20.1046 21 19V16M3 16V19C3 20.1046 3.89543 21 5 21H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </button>
                        <button
                            className="chat-window-button-minimize"
                            onClick={handleToggle}
                            aria-label={t('chat.minimize', 'Minimieren')}
                            title={t('chat.minimize', 'Minimieren')}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        <button
                            className="chat-window-button-close"
                            onClick={handleClose}
                            aria-label={t('common.close', 'Schließen')}
                            title={t('common.close', 'Schließen')}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </div>

                {isHistoryOpen && (
                    <div className="chat-history-panel" ref={historyPanelRef}>
                        <div className="chat-history-header">
                            <span>{t('chat.history', 'Verlauf')}</span>
                        </div>
                        <div className="chat-history-list">
                            {isLoadingSessions && (
                                <div className="chat-history-empty">{t('chat.loadingChats', 'Lade Chats...')}</div>
                            )}
                            {!isLoadingSessions && sessions.length === 0 && (
                                <div className="chat-history-empty">{t('chat.noChats', 'Keine Chats vorhanden.')}</div>
                            )}
                            {!isLoadingSessions && sessions.map(session => {
                                const isActive = session.id === activeSessionId;
                                return (
                                    <button
                                        key={session.id}
                                        type="button"
                                        className={`chat-history-item ${isActive ? 'active' : ''}`}
                                        onClick={() => handleSessionChange(session.id)}
                                    >
                                        <div className="chat-history-item-title">{formatSessionTitle(session)}</div>
                                        <div className="chat-history-item-meta">{formatSessionMeta(session)}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Welcome Message */}
                <div className="chat-window-welcome">
                    {isLoadingName ? (
                        <div className="chat-window-welcome-loading">
                            <div className="chat-message-typing">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    ) : (
                        <div className="chat-window-welcome-content">
                            <p className="chat-window-welcome-text">
                                {t('chat.welcomePrefix', 'Hallo')} <strong>{userName}</strong>{t('chat.welcomeSuffix', ', wie kann ich dir helfen?')}
                            </p>
                        </div>
                    )}
                </div>

                {/* Messages Area */}
                <div className="chat-window-messages">
                    {messages.length === 0 ? (
                        <div className="chat-messages-empty">
                            <p>{t('chat.emptyPrompt', 'Stelle eine Frage oder wähle einen Vorschlag.')}</p>
                        </div>
                    ) : (
                        messages.map((message, index) => renderMessage(message, index))
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="chat-window-input-area">
                    <textarea
                        ref={inputRef}
                        className="chat-window-input chat-window-input-textarea"
                        placeholder={t('chat.inputPlaceholder', 'Nachricht eingeben...')}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isSending || !chatService}
                        rows={1}
                        aria-label={t('chat.inputPlaceholder', 'Nachricht eingeben...')}
                    />
                    {isSending ? (
                        <button
                            type="button"
                            className="chat-window-cancel-button"
                            onClick={handleCancelMessage}
                            aria-label={t('chat.cancelRequest', 'Anfrage abbrechen')}
                            title={t('chat.cancelRequest', 'Anfrage abbrechen')}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>{t('common.cancel', 'Abbrechen')}</span>
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="chat-window-send-button"
                            onClick={handleSendMessage}
                            disabled={!inputValue.trim() || !chatService}
                            aria-label={t('chat.sendMessage', 'Nachricht senden')}
                            title={t('chat.sendMessage', 'Nachricht senden')}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    )}
                </div>
            </div>
            {clearChatConfirmOpen && (
                <div className="chat-clear-confirm-wrap" aria-hidden="false">
                    <DeleteConfirmModal
                        isOpen={true}
                        onClose={() => setClearChatConfirmOpen(false)}
                        onConfirm={handleConfirmClearChat}
                        title={t('chat.clearTitle', 'Chat löschen')}
                        message={t('chat.clearMessage', 'Möchtest du diesen Chat wirklich löschen? Der Chat und alle Nachrichten werden unwiderruflich gelöscht.')}
                    />
                </div>
            )}
            {traceModalMessageIndex != null && messages[traceModalMessageIndex] && (
                <div
                    className="chat-trace-overlay"
                    onClick={() => setTraceModalMessageIndex(null)}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="chat-trace-modal-title"
                    aria-label={t('chat.traceTitle', 'Was hat die KI gemacht?')}
                >
                    <div
                        ref={traceModalRef}
                        className="chat-trace-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="chat-trace-modal-header">
                            <h4 id="chat-trace-modal-title">{t('chat.traceTitle', 'Was hat die KI gemacht?')}</h4>
                            <button
                                ref={traceModalCloseRef}
                                type="button"
                                className="chat-trace-modal-close"
                                onClick={() => setTraceModalMessageIndex(null)}
                                aria-label={t('common.close', 'Schließen')}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>
                        <div className="chat-trace-modal-body">
                            {(() => {
                                const msg = messages[traceModalMessageIndex];
                                const meta = msg.metadata || {};
                                const calls = meta.function_calls || [];
                                if (calls.length === 0) {
                                    return (
                                        <p className="chat-trace-empty">
                                            {t('chat.noFunctionsCalled', 'Keine Funktionen aufgerufen.')}
                                            {meta.usedVariable && (
                                                <span> {t('chat.source', 'Quelle')}: <strong>{meta.usedVariable}</strong>
                                                    {meta.usedFilters && meta.usedFilters.year && `, ${t('chat.year', 'Jahr')} ${meta.usedFilters.year}`}
                                                    {meta.usedFilters && meta.usedFilters.month && `, ${t('chat.month', 'Monat')} ${meta.usedFilters.month}`}.
                                                </span>
                                            )}
                                        </p>
                                    );
                                }
                                return (
                                    <ol className="chat-trace-list">
                                        {calls.map((fc, idx) => (
                                            <li key={idx} className="chat-trace-item">
                                                <div className="chat-trace-fn-name">{fc.name}</div>
                                                <div className="chat-trace-args">
                                                    <pre>{JSON.stringify(fc.arguments || {}, null, 2)}</pre>
                                                </div>
                                                <div className="chat-trace-result">{formatTraceResultSummary(fc.result)}</div>
                                            </li>
                                        ))}
                                    </ol>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatWindow;
